import { z } from "zod";
import { AiReviewView, SelfReviewState } from "./advertising";
import { StatsRange } from "./analytics";
import { IsoUtc, PropertyId, Uuid } from "./primitives";

/**
 * Sponsored tasks for coins (M6; docs/07, docs/04 sections 2 and 11, docs/06 sections 5-6 and 9, ADR-0011).
 *
 * An advertiser who currently owns a store (and shows an APPROVED storefront creative there) creates a
 * campaign for that store with one or more offers. Every campaign is reviewed before it runs. A verified resident
 * starts an attempt (one active at a time); the server reserves one reward slot AND the reward coins from the
 * resident's UTC-day sponsored-coin allowance, and the claim (once per offer, ever) issues one balanced COIN journal
 * from Townsy's coin issuance.
 *
 * ADR-0029 (founder decision 2026-10-20): offers are FREE for the building owner (`billing: "free"`). Townsy gives
 * the coins and no cents move at all; ADR-0030: there is no per-store daily coin limit, only the per-visitor limits
 * (founder: "I don't think stores should have a daily coin limit."). Campaigns
 * paid before that decision (`billing: "budget"`) keep their card-funded cents budget (QuoteKind CAMPAIGN_FUNDING,
 * never Townsy Credit) and run until it is used; Townsy house campaigns keep their staff allocation.
 * Nothing in these DTOs is authority: the client never sends an amount, a reward, a "completed" flag or a quiz
 * verdict.
 */

// ---------------------------------------------------------------- vocabulary

export const OfferKind = z.enum(["website_visit", "quiz", "partner_callback"]);
export type OfferKind = z.infer<typeof OfferKind>;

/** draft -> pending_review -> approved -> active <-> paused; rejected -> (edit) -> pending_review. */
export const CampaignStatus = z.enum(["draft", "pending_review", "approved", "active", "paused", "rejected"]);
export type CampaignStatus = z.infer<typeof CampaignStatus>;
/** Why a campaign is paused. `takeover` is final: the store changed owners (ADR-0011 #5). */
export const CampaignPauseReason = z.enum(["owner", "takeover", "staff"]);
export type CampaignPauseReason = z.infer<typeof CampaignPauseReason>;
/**
 * ADR-0029: how a campaign's completions are paid for.
 *  - free: offers of a building owner (founder decision 2026-10-20). The owner pays nothing; Townsy gives the coins;
 *    every price is 0 and no cents move; no per-store daily coin limit (ADR-0030), the per-visitor limits apply.
 *  - budget: paid per completion from a card-funded cents budget (campaigns created before 2026-10-20, kept until
 *    their budget is used) or a Townsy house allocation (house campaigns).
 */
export const CampaignBilling = z.enum(["free", "budget"]);
export type CampaignBilling = z.infer<typeof CampaignBilling>;
export const OfferVersionReviewState = z.enum(["pending", "approved", "rejected"]);
export type OfferVersionReviewState = z.infer<typeof OfferVersionReviewState>;
/** active -> claimed | expired | canceled | failed (quiz tries used up). Each terminal state is final. */
export const AttemptState = z.enum(["active", "claimed", "expired", "canceled", "failed"]);
export type AttemptState = z.infer<typeof AttemptState>;

/** Card-only campaign funding bounds per order (ADR-0011 #2: minimum $5.00). */
export const CAMPAIGN_FUNDING_MIN_CENTS = 500;
export const CAMPAIGN_FUNDING_MAX_CENTS = 1_000_000;
/** Reward coins per completion can never exceed the daily sponsored-coin cap (docs/07: 100 per UTC day). */
export const OFFER_MAX_REWARD_COINS = 100;
export const OFFER_MAX_BILLABLE_CENTS = 100_000;
export const MAX_OFFERS_PER_CAMPAIGN = 3;

/**
 * ADR-0034 (founder 2026-10-22, "yes and yes"): what a building OWNER's offers can be, in one place for the screens and
 * the server. An owner's new offer, or an offer whose terms the owner changes, must be a website visit or a question
 * (no partner-confirmed "sign-up" offers) and give exactly 5 or 10 coins. Offers saved before keep running unchanged
 * until edited. Townsy house campaigns (staff quizzes) keep their own rules.
 */
export const OWNER_OFFER_KINDS = ["website_visit", "quiz"] as const;
export type OwnerOfferKind = (typeof OWNER_OFFER_KINDS)[number];
export const OWNER_OFFER_REWARD_CHOICES = [5, 10] as const;
/** The reward a new owner offer starts with (both kinds). */
export const OWNER_OFFER_DEFAULT_REWARD_COINS = 10;
export const OWNER_OFFER_KIND_MESSAGE = "Store offers can be “Visit my website” or “Answer a question”.";
export const OWNER_OFFER_REWARD_MESSAGE = "Store offers give 5 or 10 coins.";
export const isOwnerOfferKind = (kind: string): kind is OwnerOfferKind => (OWNER_OFFER_KINDS as readonly string[]).includes(kind);
export const isOwnerOfferReward = (coins: number): boolean => (OWNER_OFFER_REWARD_CHOICES as readonly number[]).includes(coins);
/** "Re-use your offers" (ADR-0030): an earlier amount that is not 5 or 10 becomes 10 if it was above 10, else 5. */
export const ownerOfferReward = (coins: number): number => (isOwnerOfferReward(coins) ? coins : coins > 10 ? 10 : 5);

/**
 * ADR-0034 addendum (founder answers 2026-10-22): partner-confirmed "sign-up" offers are switched off until they come
 * back (PARTNER_OFFERS_ENABLED). A visitor's start of one is refused with the first message; an owner's partner key
 * creation or rotation with the second.
 */
export const PARTNER_OFFER_GONE_MESSAGE = "This offer is no longer available.";
export const PARTNER_OFFERS_OFF_MESSAGE = "Sign-up offers are not available right now.";

/** Version of the advertiser consent text the owner ticks for every offer version. */
export const REWARD_CONSENT_TEXT_VERSION = "incentivized-visits-v1";
export const REWARD_CONSENT_TEXT =
  "I approve incentivized visits to my business and this exact task. Townsy rewards residents with virtual coins for completing it; " +
  "rewarded visitors may be less interested than ordinary visitors, and Townsy cannot verify what happens on my website.";

/** Honest verification labels (docs/07 section 2: never "verified reading"). */
export const OFFER_VERIFICATION_LABEL: Record<OfferKind, string> = {
  website_visit: "Eligible outbound click and return; not verified reading",
  quiz: "Correct answer to a short on-Townsy quiz; not proof of a website visit or a purchase",
  partner_callback: "Confirmed by the sponsor's signed callback",
};
/** Shown to the advertiser before activation (docs/07 section 2A). */
export const OFFER_ADVERTISER_LIMITATION: Record<OfferKind, string> = {
  website_visit:
    "Townsy can only confirm that a signed-in resident opened your approved link and asked for the reward after the minimum time. " +
    "It cannot verify that your page loaded, stayed open, or was read.",
  quiz: "Townsy checks the answer on its server. A correct answer is a completed quiz, not a website visit or a sale.",
  partner_callback:
    "Only your server's signed callback for the exact Townsy attempt marks the outcome confirmed. No callback means no reward and no charge.",
};
/** A task of a campaign paid per completion (`billing: "budget"`, ADR-0011). */
export const SPONSORED_DISCLOSURE =
  "Sponsored task: the sponsor pays Townsy for completions. Coins are a virtual reward with no cash value; they cannot be withdrawn or converted to Townsy Credit.";
/** ADR-0029: a building owner's free offer (`billing: "free"`): the owner promotes their store; Townsy gives the coins. */
export const FREE_OFFER_DISCLOSURE =
  "Store offer: the store's owner set it up to promote their business, and Townsy gives the coins. Coins are a virtual reward with no cash value; they cannot be withdrawn or converted to Townsy Credit.";
/** The disclosure shown above a list of offers (each offer also carries its own). */
export const OFFERS_DISCLOSURE =
  "Offers promote the business of the store that shows them. Coins are a virtual reward with no cash value; they cannot be withdrawn or converted to Townsy Credit.";

// ---------------------------------------------------------------- offer terms (advertiser input)

const termsBase = {
  title: z.string().trim().min(1).max(60),
  instructions: z.string().trim().min(1).max(280),
  /** Coins the resident receives per completion (independent of the billable cents; docs/04 section 11). */
  rewardCoins: z.number().int().min(1).max(OFFER_MAX_REWARD_COINS),
  /**
   * Cents charged to the campaign budget per eligible completion. ADR-0029: 0 for a free campaign (the server sets
   * it to 0 whatever is sent); at least 1 for a campaign paid from a budget.
   */
  billableCents: z.number().int().min(0).max(OFFER_MAX_BILLABLE_CENTS).default(0),
  attemptTtlSeconds: z.number().int().min(120).max(3600).default(600),
  /** Optional global cap of completions + in-flight attempts per UTC day for this offer. */
  dailyCompletionLimit: z.number().int().min(1).max(100_000).nullable().default(null),
  /** The explicit "I approve incentivized visits and this exact task" tick (ADR-0011 #1). */
  consent: z.literal(true),
};

export const OfferTerms = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("website_visit"),
      ...termsBase,
      /** Server-measured seconds between the outbound click and the claim (default 15). */
      minReturnSeconds: z.number().int().min(15).max(600).default(15),
    })
    .strict(),
  z
    .object({
      kind: z.literal("quiz"),
      ...termsBase,
      /** Short approved brand/product explanation shown with the question. */
      explanation: z.string().trim().min(1).max(280),
      question: z.string().trim().min(1).max(200),
      choices: z.array(z.string().trim().min(1).max(80)).min(2).max(4),
      /** Index into `choices`. Never sent to residents. */
      correctChoice: z.number().int().min(0).max(3),
      maxTries: z.number().int().min(1).max(3).default(2),
    })
    .strict(),
  z
    .object({
      kind: z.literal("partner_callback"),
      ...termsBase,
      /** The outcome the partner reports, e.g. "signup" (must match the signed callback). */
      conversionType: z.string().regex(/^[a-z][a-z0-9_]{1,31}$/),
    })
    .strict(),
]);
export type OfferTerms = z.infer<typeof OfferTerms>;
export type OfferTermsInput = z.input<typeof OfferTerms>;

/** POST /campaigns — a draft for a store the account currently owns, with its placed approved creative. */
export const CampaignCreateRequest = z
  .object({
    propertyId: PropertyId,
    /** An APPROVED storefront creative of the account, currently shown on that store. */
    creativeId: Uuid,
    title: z.string().trim().min(1).max(60),
    endsAt: IsoUtc.nullable().default(null),
    offers: z.array(OfferTerms).min(1).max(MAX_OFFERS_PER_CAMPAIGN),
  })
  .strict();
export type CampaignCreateRequest = z.infer<typeof CampaignCreateRequest>;

/** Staff house campaign (labelled "Townsy", quiz only, funded by an audited house allocation; ADR-0011 #1). */
export const HouseCampaignCreateRequest = z
  .object({
    propertyId: PropertyId,
    title: z.string().trim().min(1).max(60),
    endsAt: IsoUtc.nullable().default(null),
    offers: z.array(OfferTerms).min(1).max(MAX_OFFERS_PER_CAMPAIGN),
    budgetCents: z.number().int().min(1).max(CAMPAIGN_FUNDING_MAX_CENTS),
  })
  .strict();
export type HouseCampaignCreateRequest = z.infer<typeof HouseCampaignCreateRequest>;

/**
 * PATCH /campaigns/:id (owner). `offers` entries with an offerId replace that offer's terms (a NEW immutable
 * version when the terms differ; review starts again); entries without one add an offer. Title changes
 * are reviewed too. `endsAt` may be changed without review.
 */
export const CampaignUpdateRequest = z
  .object({
    expectedVersion: z.number().int().min(1),
    title: z.string().trim().min(1).max(60).optional(),
    endsAt: IsoUtc.nullable().optional(),
    offers: z
      .array(z.object({ offerId: Uuid.nullable(), terms: OfferTerms }).strict())
      .max(MAX_OFFERS_PER_CAMPAIGN)
      .optional(),
  })
  .strict();
export type CampaignUpdateRequest = z.infer<typeof CampaignUpdateRequest>;

/** POST /campaigns/:id/submit | /activate | /pause (owner) — optimistic concurrency on the campaign version. */
export const CampaignActionRequest = z.object({ expectedVersion: z.number().int().min(1) }).strict();
export type CampaignActionRequest = z.infer<typeof CampaignActionRequest>;

/** POST /review/campaigns/:id/decision (moderator/admin; never the owner). Audited. */
export const CampaignReviewDecisionRequest = z
  .object({ decision: z.enum(["approve", "reject", "suspend"]), note: z.string().max(500).default("") })
  .strict();
export type CampaignReviewDecisionRequest = z.infer<typeof CampaignReviewDecisionRequest>;

// ---------------------------------------------------------------- owner views

export const CampaignBudgetView = z.object({
  fundedCents: z.number().int().min(0),
  availableCents: z.number().int().min(0),
  reservedCents: z.number().int().min(0),
  spentCents: z.number().int().min(0),
  refundPendingCents: z.number().int().min(0),
  refundedCents: z.number().int().min(0),
  /** ADR-0037: unspent budget forfeited because the owner's account was deleted (present only when not 0). */
  forfeitedCents: z.number().int().min(0).optional(),
});
export type CampaignBudgetView = z.infer<typeof CampaignBudgetView>;

export const OfferVersionView = z.object({
  offerVersionId: Uuid,
  version: z.number().int().min(1),
  reviewState: OfferVersionReviewState,
  terms: z.object({
    kind: OfferKind,
    title: z.string(),
    instructions: z.string(),
    rewardCoins: z.number().int(),
    billableCents: z.number().int(),
    attemptTtlSeconds: z.number().int(),
    dailyCompletionLimit: z.number().int().nullable(),
    minReturnSeconds: z.number().int().nullable(),
    explanation: z.string().nullable(),
    question: z.string().nullable(),
    choices: z.array(z.string()).nullable(),
    /** Owner/staff view only. */
    correctChoice: z.number().int().nullable(),
    maxTries: z.number().int().nullable(),
    conversionType: z.string().nullable(),
  }),
  destinationHost: z.string().nullable(),
  consentAt: IsoUtc,
  createdAt: IsoUtc,
});
export type OfferVersionView = z.infer<typeof OfferVersionView>;

export const CampaignOfferView = z.object({
  offerId: Uuid,
  kind: OfferKind,
  status: z.enum(["active", "retired"]),
  current: OfferVersionView,
  verificationLabel: z.string(),
  advertiserLimitation: z.string(),
});
export type CampaignOfferView = z.infer<typeof CampaignOfferView>;

export const CampaignView = z.object({
  campaignId: Uuid,
  propertyId: PropertyId,
  address: z.string(),
  isHouse: z.boolean(),
  /** Public sponsor label: the advertiser's business name, or "Townsy" for house campaigns. */
  sponsorLabel: z.string(),
  advertiserId: Uuid.nullable(),
  creativeId: Uuid.nullable(),
  title: z.string(),
  status: CampaignStatus,
  pauseReason: CampaignPauseReason.nullable(),
  endsAt: IsoUtc.nullable(),
  version: z.number().int().min(1),
  reviewNote: z.string().nullable(),
  submittedAt: IsoUtc.nullable(),
  reviewedAt: IsoUtc.nullable(),
  budget: CampaignBudgetView,
  offers: z.array(CampaignOfferView),
  /** Why "Activate" is not possible right now (empty = can activate). */
  activationBlockers: z.array(z.string()),
  fundingMinimumCents: z.number().int(),
  createdAt: IsoUtc,
  updatedAt: IsoUtc,
  /** Moderator views only (queue, lookup, decision): the reviewer's relation to this campaign (ADR-0012). */
  selfReview: SelfReviewState.optional(),
  /**
   * ADR-0027: saved on the store's one screen, so it starts by itself once approved and funded for one completion (the
   * advertiser page's explicit Activate keeps working). Always sent by this server; optional for older fixtures.
   */
  autoStart: z.boolean().optional(),
  /**
   * ADR-0028, moderator views only: the automated review of the version waiting for review (null: none yet, or the
   * automated review is off). A suggestion in suggest mode; in auto mode it may have decided already.
   */
  aiReview: AiReviewView.nullable().optional(),
  /**
   * ADR-0029: "free" (the owner pays nothing; Townsy gives the coins) or "budget" (paid per completion). Always sent
   * by this server; absent in older fixtures, which mean "budget".
   */
  billing: CampaignBilling.optional(),
});
export type CampaignView = z.infer<typeof CampaignView>;

/**
 * POST /campaigns/:id/offers/:offerId/remove (owner; ADR-0027 "Remove this offer"): the offer is retired for good. New
 * starts stop at once; attempts already in progress may finish under their reserved terms until they expire; visitors no
 * longer see it; the campaign's budget stays for its other or future offers (no money moves). Removing an offer that is
 * already removed changes nothing (`removed: false`).
 */
export const OfferRemoveRequest = z.object({}).strict();
export type OfferRemoveRequest = z.infer<typeof OfferRemoveRequest>;
export const OfferRemoveResponse = z.object({ campaign: CampaignView, removed: z.boolean() });
export type OfferRemoveResponse = z.infer<typeof OfferRemoveResponse>;
export const MyCampaignsResponse = z.object({ items: z.array(CampaignView) });
export type MyCampaignsResponse = z.infer<typeof MyCampaignsResponse>;

export const CampaignReviewQueueResponse = z.object({ items: z.array(CampaignView), nextCursor: z.string().nullable() });
export type CampaignReviewQueueResponse = z.infer<typeof CampaignReviewQueueResponse>;

// ---------------------------------------------------------------- resident views

/** Why a resident can or cannot start an offer right now. */
export const OfferEligibility = z.enum([
  "eligible",
  /** Guests see offers but must create an account to earn (docs/07 section 4). */
  "sign_in_required",
  "email_unverified",
  "already_earned",
  "quiz_failed",
  "daily_cap_reached",
  "daily_limit_reached",
  "budget_exhausted",
  "own_campaign",
  "attempt_in_progress",
  "too_close_to_midnight",
  "unavailable",
]);
export type OfferEligibility = z.infer<typeof OfferEligibility>;

export const PublicOfferView = z.object({
  offerId: Uuid,
  campaignId: Uuid,
  propertyId: PropertyId,
  address: z.string(),
  sponsorLabel: z.string(),
  isHouse: z.boolean(),
  kind: OfferKind,
  title: z.string(),
  instructions: z.string(),
  rewardCoins: z.number().int().min(1),
  /** Destination host of a website/partner task (the full URL is only ever reached through /out/<token>). */
  destinationHost: z.string().nullable(),
  minReturnSeconds: z.number().int().nullable(),
  explanation: z.string().nullable(),
  verificationLabel: z.string(),
  disclosure: z.string(),
  eligibility: OfferEligibility,
  /** The viewer's own active attempt on this offer, if any. */
  activeAttemptId: Uuid.nullable(),
});
export type PublicOfferView = z.infer<typeof PublicOfferView>;

export const DailyRewardUsage = z.object({
  /** UTC date the allowance applies to. */
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  capCoins: z.number().int().min(0),
  grantedCoins: z.number().int().min(0),
  reservedCoins: z.number().int().min(0),
});
export type DailyRewardUsage = z.infer<typeof DailyRewardUsage>;

export const AttemptView = z.object({
  attemptId: Uuid,
  offerId: Uuid,
  campaignId: Uuid,
  propertyId: PropertyId,
  kind: OfferKind,
  state: AttemptState,
  title: z.string(),
  sponsorLabel: z.string(),
  rewardCoins: z.number().int(),
  startedAt: IsoUtc,
  expiresAt: IsoUtc,
  /** Same-origin /out/<token> for website/partner tasks; only returned by start and refresh-link calls. */
  outboundUrl: z.string().nullable(),
  destinationHost: z.string().nullable(),
  clickedAt: IsoUtc.nullable(),
  /** Website task: the earliest server time a claim can succeed (null until the link was opened). */
  claimableAt: IsoUtc.nullable(),
  quiz: z
    .object({ explanation: z.string(), question: z.string(), choices: z.array(z.string()), triesLeft: z.number().int().min(0) })
    .nullable(),
  partner: z.object({ conversionType: z.string(), confirmed: z.boolean() }).nullable(),
  verificationLabel: z.string(),
  resolution: z.string().nullable(),
});
export type AttemptView = z.infer<typeof AttemptView>;

export const PropertyOffersResponse = z.object({
  propertyId: PropertyId,
  disclosure: z.string(),
  items: z.array(PublicOfferView),
  /** Null for guests. */
  daily: DailyRewardUsage.nullable(),
});
export type PropertyOffersResponse = z.infer<typeof PropertyOffersResponse>;

/** GET /me/earn — the "Earn coins" list of active sponsored tasks across the town. */
export const EarnCoinsResponse = z.object({
  disclosure: z.string(),
  items: z.array(PublicOfferView),
  daily: DailyRewardUsage.nullable(),
  activeAttempt: AttemptView.nullable(),
  notes: z.array(z.string()),
});
export type EarnCoinsResponse = z.infer<typeof EarnCoinsResponse>;

/** POST /offers/:id/attempts (Idempotency-Key). The body is empty: the server decides everything. */
export const AttemptStartRequest = z.object({}).strict();
export type AttemptStartRequest = z.infer<typeof AttemptStartRequest>;
export const AttemptStartResponse = z.object({ attempt: AttemptView, replayed: z.boolean() });
export type AttemptStartResponse = z.infer<typeof AttemptStartResponse>;

/** POST /offer-attempts/:id/claim. Quiz tasks send the chosen answer index; nothing else is accepted. */
export const AttemptClaimRequest = z.object({ answerIndex: z.number().int().min(0).max(3).optional() }).strict();
export type AttemptClaimRequest = z.infer<typeof AttemptClaimRequest>;
export const AttemptClaimResponse = z.object({
  attempt: AttemptView,
  claim: z.object({ claimId: Uuid, rewardCoins: z.number().int(), coinsAvailableAfter: z.number().int(), claimedAt: IsoUtc }),
  /** True when this claim had already been made (a repeated request awards nothing new). */
  replayed: z.boolean(),
});
export type AttemptClaimResponse = z.infer<typeof AttemptClaimResponse>;

// ---------------------------------------------------------------- partner callbacks (docs/06 section 9)

/** Header names of a signed partner callback (lower-case as Node delivers them). */
export const PARTNER_HEADERS = {
  keyId: "townsy-key-id",
  timestamp: "townsy-timestamp",
  nonce: "townsy-nonce",
  signature: "townsy-signature",
} as const;
export const PARTNER_SIGNATURE_SCHEME = "v1";
/** Maximum |server time - signed timestamp| (and the nonce replay window). */
export const PARTNER_TIMESTAMP_TOLERANCE_SECONDS = 300;
export const PARTNER_MAX_BODY_BYTES = 8_192;

/** JSON body of POST /partners/events. */
export const PartnerEventBody = z
  .object({
    /** The partner's own unique event id (idempotency: a retry with the same id is acknowledged, never re-awarded). */
    eventId: z.string().min(1).max(100).regex(/^[A-Za-z0-9._:-]+$/),
    /** The short-lived attempt token Townsy appended to the partner link (`townsy_attempt`). */
    attemptToken: z.string().min(16).max(128).regex(/^[A-Za-z0-9_-]+$/),
    conversionType: z.string().regex(/^[a-z][a-z0-9_]{1,31}$/),
    occurredAt: IsoUtc.optional(),
  })
  .strict();
export type PartnerEventBody = z.infer<typeof PartnerEventBody>;

export const PartnerEventAck = z.object({
  received: z.literal(true),
  eventId: z.string(),
  /** `confirmed`: the outcome was recorded now; `duplicate`: already recorded earlier (idempotent retry). */
  outcome: z.enum(["confirmed", "duplicate"]),
});
export type PartnerEventAck = z.infer<typeof PartnerEventAck>;

export const PartnerKeyView = z.object({
  keyId: z.string(),
  advertiserId: Uuid,
  campaignId: Uuid.nullable(),
  scopes: z.array(z.string()),
  status: z.enum(["active", "revoked"]),
  createdAt: IsoUtc,
  /** Set when the key was rotated: it stops working at this time. */
  expiresAt: IsoUtc.nullable(),
  revokedAt: IsoUtc.nullable(),
  lastUsedAt: IsoUtc.nullable(),
});
export type PartnerKeyView = z.infer<typeof PartnerKeyView>;
export const PartnerKeyCreateRequest = z.object({ advertiserId: Uuid, campaignId: Uuid.nullable().default(null) }).strict();
export type PartnerKeyCreateRequest = z.infer<typeof PartnerKeyCreateRequest>;
/** The secret is shown exactly once; Townsy cannot show it again (rotate instead). */
export const PartnerKeyCreateResponse = z.object({ key: PartnerKeyView, secret: z.string(), signingGuide: z.array(z.string()) });
export type PartnerKeyCreateResponse = z.infer<typeof PartnerKeyCreateResponse>;

// ---------------------------------------------------------------- reports (docs/07 sections 5-7)

export const CampaignReportMetrics = z.object({
  attemptsStarted: z.number().int().min(0),
  completionsWebsiteVisit: z.number().int().min(0),
  completionsQuiz: z.number().int().min(0),
  completionsPartner: z.number().int().min(0),
  completionsTotal: z.number().int().min(0),
  /** Attempts that ended without a reward (expired, canceled, quiz tries used up): their budget was released. */
  releasedAttempts: z.number().int().min(0),
  coinsIssued: z.number().int().min(0),
  spentCents: z.number().int().min(0),
  /** Outbound clicks tied to a disclosed sponsored attempt (separate from ordinary clicks). */
  rewardedOutboundClicks: z.number().int().min(0),
});
export type CampaignReportMetrics = z.infer<typeof CampaignReportMetrics>;

export const CampaignReportResponse = z.object({
  campaignId: Uuid,
  title: z.string(),
  definitionsVersion: z.number().int(),
  timezone: z.literal("UTC"),
  generatedAt: IsoUtc,
  /** ADR-0029: a free campaign's completions cost nothing (spentCents stays 0; no spend line is shown). */
  billing: CampaignBilling.optional(),
  budget: CampaignBudgetView,
  ranges: z.record(StatsRange, CampaignReportMetrics),
  offers: z.array(
    z.object({
      offerId: Uuid,
      kind: OfferKind,
      verificationLabel: z.string(),
      versions: z.array(z.object({ version: z.number().int(), rewardCoins: z.number().int(), billableCents: z.number().int() })),
      ranges: z.record(StatsRange, CampaignReportMetrics),
    }),
  ),
  notes: z.array(z.string()),
});
export type CampaignReportResponse = z.infer<typeof CampaignReportResponse>;
